/* iter55u — Small presentational bits extracted from
 * ProviderProbeControls.jsx to lower its cyclomatic complexity.
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, XCircle, Clock } from 'lucide-react';
import { timeAgo } from '@/lib/format';

/** Row: `alert email` input + Save button. */
export function AlertEmailRow({
  alertEmail, onAlertEmailChange, onSave, saving, testIdPrefix,
}) {
  return (
    <div>
      <Label className="text-[11px]">
        Alert email (optional)
        <span className="ml-1 text-muted-foreground font-normal">
          — get an email when a probe flips from{' '}
          <span className="text-emerald-700">OK</span> to{' '}
          <span className="text-rose-700">failing</span>
        </span>
      </Label>
      <div className="mt-1 flex gap-2">
        <Input
          type="email"
          value={alertEmail || ''}
          onChange={(e) => onAlertEmailChange(e.target.value)}
          placeholder="ops@yourcompany.com"
          className="h-8 max-w-md text-[11.5px]"
          data-testid={`${testIdPrefix}-alert-email-input`}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={onSave}
          disabled={saving}
          className="h-8 gap-1.5"
          data-testid={`${testIdPrefix}-alert-email-save`}
        >
          Save email
        </Button>
      </div>
    </div>
  );
}

/** Small `Last tested Xm ago ✓` chip next to the Test button. */
export function LastTestStamp({ ok, at, message, testIdPrefix }) {
  if (!at) return null;
  const cls = ok === true
    ? 'text-emerald-700'
    : ok === false
      ? 'text-rose-700'
      : 'text-muted-foreground';
  return (
    <span
      data-testid={`${testIdPrefix}-last-test-stamp`}
      className={`inline-flex items-center gap-1 text-[10.5px] ${cls}`}
      title={message || ''}
    >
      <Clock className="h-3 w-3" />
      Last tested {timeAgo(at)}
      {ok === true && ' ✓'}
      {ok === false && ' ✗'}
    </span>
  );
}

/** Coloured result banner shown once a probe finishes. */
export function ProbeResultPanel({ result, testIdPrefix }) {
  if (!result) return null;
  const shell = result.ok
    ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
    : 'border-rose-300 bg-rose-50 text-rose-900';
  const Icon = result.ok ? CheckCircle2 : XCircle;
  return (
    <div
      data-testid={`${testIdPrefix}-test-result`}
      className={`flex items-start gap-2 rounded-md border px-3 py-2 text-[11px] ${shell}`}
    >
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <div className="space-y-0.5">
        <div className="font-medium">{result.message}</div>
        <div className="text-[10px] opacity-75">
          stage: <code className="font-mono-stat">{result.stage || '—'}</code>
          {result.status != null && (
            <> · HTTP <code className="font-mono-stat">{result.status}</code></>
          )}
        </div>
      </div>
    </div>
  );
}
